import { useState } from "react";
import { toast } from "sonner";
import { Loader2, HandCoins } from "lucide-react";
import { Reveal } from "./Reveal";
import { post, loadRazorpay, openRazorpay } from "@/lib/api";

const PRESETS = [100, 500, 1000, 5000];
const EMPTY = { name: "", email: "", phone: "", message: "" };

export const Donation = () => {
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [attempt, setAttempt] = useState(0);
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: false });
  };
  const errCls = (k) => (errors[k] ? ` field-error bounce-${attempt % 2}` : "");
  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = true;
    if (!finalAmount || finalAmount <= 0) errs.amount = true;
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      setAttempt((a) => a + 1);
      toast.error(errs.amount ? "Please choose a valid donation amount." : "Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const { data: order } = await post("/payments/razorpay/order", { purpose: "donation", amount: finalAmount, payload: form });
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load the payment gateway. Please try again.");
        return;
      }
      openRazorpay({
        order,
        name: form.name,
        email: form.email,
        phone: form.phone,
        onSuccess: async (res) => {
          try {
            await post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(`Thank you, ${form.name}! Your donation of ₹${finalAmount.toLocaleString("en-IN")} has been received.`, { duration: 8000 });
            setForm(EMPTY);
            setCustom("");
          } catch {
            toast.error("Payment verification failed. Please contact the party office.");
          }
        },
        onDismiss: () => toast.info("Payment cancelled."),
      });
    } catch (err) {
      if (err.response?.status === 503) {
        try {
          await post("/donations/pledge", { ...form, amount: finalAmount });
          toast.success(`Thank you, ${form.name}! Your pledge of ₹${finalAmount.toLocaleString("en-IN")} has been recorded. The party office will reach out to complete the donation.`, { duration: 8000 });
          setForm(EMPTY);
          setCustom("");
        } catch {
          toast.error("Could not record your pledge. Please try again.");
        }
      } else {
        toast.error("Could not start the donation. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="relative py-28 sec-donate overflow-hidden" data-testid="donation-section">
      <div className="absolute top-1/3 left-0 w-[460px] h-[460px] rounded-full bg-yellow-400/8 blur-[130px]" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <Reveal>
            <p className="eyebrow mb-4">// Fund the Movement</p>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Let's Contribute Together<br /><span className="text-yellow-400">For the Goa We Believe In.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
              RGP's work is made possible by people who choose to participate, contribute
              and be part of building a better Goa.
            </p>
            <p className="mt-3 text-zinc-400 leading-relaxed max-w-lg">
              Your contribution helps us take our work to the ground and strengthen
              people's participation.
            </p>
            <p className="mt-3 text-yellow-400 font-display font-bold uppercase text-lg tracking-wide">
              Every contribution makes a difference.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="agenda-card p-8 lg:p-10" data-testid="donation-form">
              <div className="flex items-center gap-3 mb-6">
                <HandCoins className="text-yellow-400" size={24} />
                <h3 className="font-display font-bold uppercase text-2xl">Donate to RGP</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`donation-preset-${p}`}
                    className={`amount-chip ${!custom && amount === p ? "active" : ""}`}
                    onClick={() => { setAmount(p); setCustom(""); }}
                  >
                    ₹{p.toLocaleString("en-IN")}
                  </button>
                ))}
                <input
                  data-testid="donation-input-custom"
                  className={`rgp-input !w-36${errCls("amount")}`}
                  type="number"
                  min="1"
                  placeholder="Custom ₹"
                  value={custom}
                  onChange={(e) => {
                    setCustom(e.target.value);
                    if (errors.amount) setErrors({ ...errors, amount: false });
                  }}
                />
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <input data-testid="donation-input-name" className={`rgp-input${errCls("name")}`} placeholder="Full Name *" value={form.name} onChange={set("name")} />
                <input data-testid="donation-input-phone" className="rgp-input" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input data-testid="donation-input-email" className={`rgp-input sm:col-span-2${errCls("email")}`} type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
                <textarea data-testid="donation-input-message" className="rgp-input sm:col-span-2 min-h-[70px]" placeholder="Message of support (optional)" value={form.message} onChange={set("message")} />
              </div>
              <button data-testid="donation-submit-button" disabled={loading} className="btn-yellow w-full justify-center mt-6 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                {loading ? "Processing..." : `Donate ₹${(finalAmount || 0).toLocaleString("en-IN")}`}
              </button>
              <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                Payments are processed securely via UPI, cards and netbanking.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
